import { useState } from "react";
import { PROCEDURE_CATEGORY_COLOURS } from "@dentacore/shared-types";
import type { ProcedureCategory, ProcedureCategoryColour } from "@dentacore/shared-types";

import {
  createProcedureCategory,
  orderProcedureCategories,
  removeProcedureCategory,
  updateProcedureCategory,
} from "../api/procedure-categories";
import { Modal } from "../ui/modal";
import styles from "./procedures.module.css";

/** A palette key as the CSS that draws it — a token, never a colour value (PG-D3). */
export function categoryColour(colour: ProcedureCategoryColour): string {
  return `var(--c-cat-${colour})`;
}

/** What each swatch is called, for a screen reader and the tooltip. */
const COLOUR_NAMES: Record<ProcedureCategoryColour, string> = {
  blue: "Blue",
  indigo: "Indigo",
  violet: "Violet",
  magenta: "Magenta",
  red: "Red",
  yellow: "Yellow",
  black: "Black",
  green: "Green",
  amber: "Amber",
  olive: "Olive",
  slate: "Slate",
};

/**
 * The clinic's procedure categories — STORY-176b, PG-D1–PG-D5.
 *
 * **One list for the whole clinic** (PG-D2), so it is the same whichever
 * location the page is on. Each row is a name typed in place (saved when the
 * field is left, like a visit count — TR5), a colour picked from our palette,
 * a move up or down, and a remove that the server refuses while procedures use
 * it (PG-D5) — its sentence names how many, and is shown here as it comes.
 *
 * **Writes straight to the server and reports locally**, rather than through
 * the page's toast: the toast sits under the dialog, and a refusal the person
 * cannot see is a refusal they will retry.
 */
export function CategoriesDialog({
  categories,
  onChanged,
  onClose,
}: {
  categories: readonly ProcedureCategory[];
  /** The list as it now stands, after any write. */
  onChanged: () => Promise<void>;
  onClose: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  // The first colour nobody is using yet, so a new one stands apart by default.
  const used = new Set(categories.map((row) => row.colour));
  const [newColour, setNewColour] = useState<ProcedureCategoryColour>(
    PROCEDURE_CATEGORY_COLOURS.find((key) => !used.has(key)) ?? PROCEDURE_CATEGORY_COLOURS[0],
  );

  const write = async (action: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onChanged();
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That change could not be saved.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const move = (index: number, by: -1 | 1): void => {
    const ids = categories.map((row) => row.id);
    const [moved] = ids.splice(index, 1);
    if (moved === undefined) return;
    ids.splice(index + by, 0, moved);
    void write(() => orderProcedureCategories(ids));
  };

  return (
    <Modal
      label="Procedure categories"
      onClose={onClose}
      testId="proc-categories-dialog"
      panelClassName={styles.dialog}
    >
      <div className={styles.dialogForm}>
        <h2 className={styles.dialogTitle}>Procedure categories</h2>
        <p className={styles.categoriesNote}>
          Every procedure belongs to one. Its colour marks that procedure&rsquo;s bookings on the
          calendar.
        </p>

        <ul className={styles.categoryList} data-testid="proc-categories">
          {categories.map((row, index) => (
            <li key={row.id} className={styles.categoryItem} data-testid="proc-category-row">
              <div className={styles.categoryRow}>
                <button
                  type="button"
                  className={styles.swatchButton}
                  style={{ "--swatch": categoryColour(row.colour) } as React.CSSProperties}
                  aria-label={`Colour of ${row.name}: ${COLOUR_NAMES[row.colour]}. Change it`}
                  aria-expanded={picking === row.id}
                  disabled={busy}
                  onClick={() => setPicking((was) => (was === row.id ? null : row.id))}
                  data-testid="proc-category-colour"
                />
                <NameField
                  key={row.name}
                  name={row.name}
                  busy={busy}
                  onSave={(name) => write(() => updateProcedureCategory(row.id, { name }))}
                />
                <span className={styles.categoryCount}>
                  {row.procedureCount === 1
                    ? "1 procedure"
                    : `${String(row.procedureCount)} procedures`}
                </span>
                <button
                  type="button"
                  className={styles.iconButton}
                  disabled={busy || index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={`Move ${row.name} up`}
                  data-testid="proc-category-up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className={styles.iconButton}
                  disabled={busy || index === categories.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={`Move ${row.name} down`}
                  data-testid="proc-category-down"
                >
                  ↓
                </button>
                <button
                  type="button"
                  className={styles.iconButton}
                  disabled={busy}
                  onClick={() => void write(() => removeProcedureCategory(row.id))}
                  aria-label={`Remove ${row.name}`}
                  title="Remove"
                  data-testid="proc-category-remove"
                >
                  ×
                </button>
              </div>
              {picking === row.id && (
                <Palette
                  value={row.colour}
                  busy={busy}
                  onPick={(colour) => {
                    void write(() => updateProcedureCategory(row.id, { colour })).then((landed) => {
                      if (landed) setPicking(null);
                    });
                  }}
                />
              )}
            </li>
          ))}
        </ul>

        {error !== null && (
          <p className={styles.fieldError} role="alert" data-testid="proc-categories-error">
            {error}
          </p>
        )}

        <form
          className={styles.categoryAdd}
          onSubmit={(event) => {
            event.preventDefault();
            if (newName.trim() === "") return;
            void write(() =>
              createProcedureCategory({ name: newName.trim(), colour: newColour }),
            ).then((landed) => {
              if (landed) setNewName("");
            });
          }}
        >
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Add a category</span>
            <input
              className={styles.input}
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="Name"
              maxLength={60}
              data-testid="proc-category-new-name"
            />
          </label>
          <Palette value={newColour} busy={busy} onPick={setNewColour} />
          <button
            type="submit"
            className={styles.primary}
            disabled={busy || newName.trim() === ""}
            data-testid="proc-category-add"
          >
            Add it
          </button>
        </form>

        <div className={styles.dialogActions}>
          <button type="button" className={styles.ghost} onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * A name, edited in place and saved when the field is left or Enter is
 * pressed — never per keystroke, so a half-typed name is never saved. A blank
 * one goes back to what it was.
 */
function NameField({
  name,
  busy,
  onSave,
}: {
  name: string;
  busy: boolean;
  onSave: (name: string) => Promise<boolean>;
}): React.JSX.Element {
  const [text, setText] = useState(name);
  const commit = (): void => {
    const next = text.trim();
    if (next === "" || next === name) {
      setText(name);
      return;
    }
    void onSave(next).then((landed) => {
      if (!landed) setText(name);
    });
  };
  return (
    <input
      className={`${styles.input} ${styles.categoryName}`}
      value={text}
      disabled={busy}
      maxLength={60}
      aria-label="Category name"
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      data-testid="proc-category-name"
    />
  );
}

/** Our eight colours, as a row of swatches (PG-D3). The chosen one is ringed. */
function Palette({
  value,
  busy,
  onPick,
}: {
  value: ProcedureCategoryColour;
  busy: boolean;
  onPick: (colour: ProcedureCategoryColour) => void;
}): React.JSX.Element {
  return (
    <div className={styles.palette} role="radiogroup" aria-label="Colour">
      {PROCEDURE_CATEGORY_COLOURS.map((key) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={key === value}
          aria-label={COLOUR_NAMES[key]}
          title={COLOUR_NAMES[key]}
          className={key === value ? `${styles.swatch} ${styles.swatchOn}` : styles.swatch}
          style={{ "--swatch": categoryColour(key) } as React.CSSProperties}
          disabled={busy}
          onClick={() => onPick(key)}
          data-testid={`proc-swatch-${key}`}
        />
      ))}
    </div>
  );
}
